import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, Plus } from 'lucide-react';
import { API, authFetch } from '@/App';

export default function BrandAutocomplete({ value, onChange, category, className = '' }) {
  const [brands, setBrands] = useState([]);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isCustom, setIsCustom] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchBrands();
  }, [category]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchBrands = async () => {
    try {
      const params = category ? `?category=${category}` : '';
      const res = await authFetch(`${API}/products/brands-for-category${params}`);
      if (res.ok) setBrands(await res.json());
    } catch (e) {
      console.error('Error fetching brands:', e);
    }
  };

  const filtered = brands.filter(b =>
    !query || b.brand.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = (brand) => {
    onChange(brand);
    setQuery('');
    setIsOpen(false);
    setIsCustom(false);
  };

  const handleAddNew = () => {
    setIsCustom(true);
    setIsOpen(false);
    onChange('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  if (isCustom) {
    return (
      <div className={`relative ${className}`}>
        <input
          ref={inputRef}
          data-testid="brand-custom-input"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type new brand name *"
          className="w-full px-4 py-2.5 rounded-xl border border-primary bg-primary/5 focus:border-primary outline-none text-sm pr-20"
        />
        <button
          type="button"
          onClick={() => { setIsCustom(false); onChange(''); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-primary font-medium hover:underline"
        >
          Pick existing
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <button
        type="button"
        data-testid="brand-autocomplete-trigger"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl border border-border bg-white/50 hover:border-primary/50 transition-colors text-sm text-left"
      >
        <span className={value ? 'text-foreground' : 'text-muted-foreground/40'}>
          {value || 'Select brand *'}
        </span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-xl border border-border shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b border-border sticky top-0 bg-white">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                data-testid="brand-search-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search brands..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-border text-sm outline-none focus:border-primary"
                autoFocus
              />
            </div>
          </div>

          <div className="overflow-y-auto max-h-44">
            {filtered.length > 0 ? (
              filtered.map(b => (
                <button
                  key={b.brand}
                  type="button"
                  data-testid={`brand-option-${b.brand.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => handleSelect(b.brand)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-primary/5 transition-colors flex items-center justify-between ${
                    value === b.brand ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
                  }`}
                >
                  <span className="truncate">{b.brand}</span>
                  <span className="text-xs text-muted-foreground shrink-0 ml-2">{b.count}</span>
                </button>
              ))
            ) : (
              <p className="px-4 py-3 text-sm text-muted-foreground text-center">
                No brands match "{query}"
              </p>
            )}
          </div>

          <div className="border-t border-border sticky bottom-0 bg-white">
            <button
              type="button"
              data-testid="brand-add-new"
              onClick={handleAddNew}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-primary font-medium hover:bg-primary/5 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add new brand
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
